'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { LayoutDashboard, Building2, ClipboardList, ShieldCheck, FileText, Activity, AlertTriangle, RefreshCw, BarChart3, Settings, ChevronLeft, ChevronRight, ChevronDown, FilePlus, ShoppingCart, PieChart, Bell  } from 'lucide-react';
import { useRole } from '@/contexts/RoleContext';
import { ROLE_NAMES } from '@/components/RoleSwitcher';
import Icon from '@/components/ui/AppIcon';



interface NavItem {
  key: string;
  label: string;
  href: string;
  icon: React.ElementType;
  badge?: number;
  badgeVariant?: 'critical' | 'warning' | 'info';
}

interface NavGroup {
  key: string;
  label: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    key: 'group-overview',
    label: 'Overview',
    items: [
      { key: 'nav-dashboard', label: 'Dashboard', href: '/executive-dashboard', icon: LayoutDashboard },
      { key: 'nav-notifications', label: 'Notifications', href: '/notifications', icon: Bell, badge: 6, badgeVariant: 'critical' },
    ],
  },
  {
    key: 'group-lifecycle',
    label: 'Lifecycle',
    items: [
      { key: 'nav-vendors', label: 'Vendors', href: '/vendor-management', icon: Building2 },
      { key: 'nav-intake', label: 'Intake Queue', href: '/vendor-management', icon: ClipboardList, badge: 3, badgeVariant: 'info' },
      { key: 'nav-new-intake', label: 'New Intake', href: '/intake-form', icon: FilePlus },
      { key: 'nav-sourcing', label: 'Sourcing', href: '/sourcing-workspace', icon: ShoppingCart },
      { key: 'nav-dd', label: 'Due Diligence', href: '/due-diligence-workspace', icon: ShieldCheck, badge: 8, badgeVariant: 'warning' },
      { key: 'nav-contracts', label: 'Contracts', href: '/contracts', icon: FileText },
      { key: 'nav-monitoring', label: 'Monitoring', href: '/monitoring', icon: Activity },
    ],
  },
  {
    key: 'group-risk',
    label: 'Risk & Compliance',
    items: [
      { key: 'nav-issues', label: 'Issues', href: '/issues', icon: AlertTriangle, badge: 3, badgeVariant: 'critical' },
      { key: 'nav-renewals', label: 'Renewals', href: '/vendor-management', icon: RefreshCw, badge: 2, badgeVariant: 'warning' },
    ],
  },
  {
    key: 'group-ops',
    label: 'Operations',
    items: [
      { key: 'nav-analytics', label: 'Analytics', href: '/analytics', icon: PieChart },
      { key: 'nav-reports', label: 'Reports', href: '/reports', icon: BarChart3 },
      { key: 'nav-admin', label: 'Administration', href: '/executive-dashboard', icon: Settings },
    ],
  },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const pathname = usePathname();
  const { canRoute, role, roleMeta } = useRole();
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
    'group-overview': true,
    'group-lifecycle': true,
    'group-risk': true,
    'group-ops': true,
  });

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const isActive = (href: string) => pathname === href;

  // Filter nav groups to only show items the current role can access
  const filteredGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => canRoute(item.href)),
  })).filter((group) => group.items.length > 0);

  const userName = ROLE_NAMES[role] ?? 'User';

  return (
    <aside
      className={`
        flex flex-col h-full transition-all duration-300 ease-in-out overflow-hidden
        border-r border-[hsl(var(--border))] bg-[hsl(var(--surface))]
        ${collapsed ? 'w-16' : 'w-60'}
      `}
    >
      {/* Logo */}
      <div
        className={`
          flex items-center h-14 px-3 border-b border-[hsl(var(--border))] flex-shrink-0
          ${collapsed ? 'justify-center' : 'gap-2.5'}
        `}
      >
        <Link href="/executive-dashboard" className="flex items-center gap-2 min-w-0 hover:opacity-80 transition-opacity">
          <AppLogo size={28} />
          {!collapsed && (
            <div className="min-w-0">
              <span className="text-sm font-bold text-[hsl(var(--text-primary))] leading-none block">
                CityTPRM
              </span>
              <span className="text-2xs text-[hsl(var(--text-muted))] leading-none block mt-0.5">
                City of Pleasanton
              </span>
            </div>
          )}
        </Link>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-4">
        {filteredGroups.map((group) => (
          <div key={group.key}>
            {!collapsed && (
              <button
                onClick={() => toggleGroup(group.key)}
                className="w-full flex items-center justify-between px-2 mb-1 group"
              >
                <span className="section-label">{group.label}</span>
                <ChevronDown
                  size={12}
                  className={`text-[hsl(var(--text-muted))] transition-transform duration-200 ${
                    expandedGroups[group.key] ? '' : '-rotate-90'
                  }`}
                />
              </button>
            )}
            {(collapsed || expandedGroups[group.key]) && (
              <ul className="space-y-0.5">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  return (
                    <li key={item.key}>
                      <Link
                        href={item.href}
                        className={`
                          nav-item relative group
                          ${active ? 'active' : ''}
                          ${collapsed ? 'justify-center px-2' : ''}
                        `}
                        title={collapsed ? item.label : undefined}
                      >
                        <Icon size={16} className="flex-shrink-0" />
                        {!collapsed && (
                          <>
                            <span className="flex-1 text-sm">{item.label}</span>
                            {item.badge !== undefined && (
                              <span
                                className={`
                                  text-2xs font-semibold px-1.5 py-0.5 rounded-full tabular-nums
                                  ${item.badgeVariant === 'critical' ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400' : ''}
                                  ${item.badgeVariant === 'warning' ? 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400' : ''}
                                  ${item.badgeVariant === 'info' ? 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400' : ''}
                                `}
                              >
                                {item.badge}
                              </span>
                            )}
                          </>
                        )}
                        {collapsed && item.badge !== undefined && (
                          <span className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-red-500" />
                        )}
                        {collapsed && (
                          <span className="
                            absolute left-full ml-2 px-2 py-1 text-xs font-medium rounded-md
                            bg-[hsl(var(--text-primary))] text-[hsl(var(--surface))]
                            opacity-0 group-hover:opacity-100 pointer-events-none
                            transition-opacity duration-150 whitespace-nowrap z-50
                          ">
                            {item.label}
                          </span>
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="border-t border-[hsl(var(--border))] p-2 flex-shrink-0">
        {!collapsed && (
          <div className="flex items-center gap-2 px-2 py-2 mb-1">
            <div className={`w-7 h-7 rounded-full ${roleMeta.color} flex items-center justify-center text-white text-xs font-semibold flex-shrink-0`}>
              {roleMeta.initials}
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-[hsl(var(--text-primary))] truncate">{userName}</p>
              <p className="text-2xs text-[hsl(var(--text-muted))] truncate">{role}</p>
            </div>
          </div>
        )}
        <button
          onClick={onToggle}
          className="btn-ghost w-full justify-center mt-1"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          {!collapsed && <span className="text-xs">Collapse</span>}
        </button>
      </div>
    </aside>
  );
}